import { useState } from 'react';
import { Card, KpiCard, Modal, SlidePanel, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import styles from './MpCleaning.module.css';

interface Task {
  id: string;
  property_name: string;
  scheduled_date: string;
  time_start: string | null;
  time_end: string | null;
  vendor: string | null;
  status: string;
  checkout_time: string | null;
  checkin_time: string | null;
  notes?: string;
  property_id?: string;
  listing_id?: string;
  cost?: number;
}

interface Listing {
  id: string;
  property_id: string;
  property_name: string;
}

interface Staff {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  type: string;
  cost_per_task: number;
  is_active: boolean;
  notes: string | null;
}

const statusLabel: Record<string, string> = {
  completed: '完了', in_progress: '作業中', scheduled: '予定', urgent: '緊急', unassigned: '未割当',
};

function fmt(t: string | null): string {
  if (!t) return '—';
  return t.slice(0, 5);
}

interface ChecklistItem { id: string; section: string; item: string; sort_order: number; }

const defaultChecklist = [
  { section: 'ベッドルーム', items: ['リネン交換・ベッドメイク', '枕カバー・毛布カバー交換', '床掃除機・拭き掃除', 'クローゼット点検'] },
  { section: 'バス・トイレ', items: ['タオル類交換', 'バス・洗面台清掃', 'トイレ清掃・消耗品補充', 'アメニティ補充確認'] },
  { section: 'キッチン・リビング', items: ['食器類洗浄・収納', 'ゴミ回収・ゴミ箱清掃', '電子レンジ・冷蔵庫内点検'] },
  { section: '最終確認', items: ['備品数確認（紛失チェック）', '鍵・スマートロック確認', '写真撮影・報告'] },
];

export function MpCleaning() {
  const { data: tasks, loading, refetch } = useApi<Task[]>('/tenant/minpaku/cleaning');
  const { data: listings } = useApi<Listing[]>('/tenant/minpaku/listings');
  const { data: staffList, refetch: refetchStaff } = useApi<Staff[]>('/tenant/minpaku/cleaning-staff');
  const { data: checklistData, refetch: refetchChecklist } = useApi<ChecklistItem[]>('/tenant/minpaku/cleaning-checklist');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showStaff, setShowStaff] = useState(false);
  const [showStaffAdd, setShowStaffAdd] = useState(false);
  const [editTarget, setEditTarget] = useState<Task | null>(null);
  const [editForm, setEditForm] = useState({ vendor: '', time_start: '', time_end: '', notes: '' });
  const [staffForm, setStaffForm] = useState({ name: '', phone: '', email: '', type: 'vendor', cost_per_task: '' });
  const [editingChecklist, setEditingChecklist] = useState(false);
  const [editChecklist, setEditChecklist] = useState<{ section: string; items: string[] }[]>([]);

  const allStaff = staffList ?? [];

  // チェックリスト: DBにデータがあればそれを使用、なければデフォルト
  const checklistTemplate = (() => {
    const items = checklistData ?? [];
    if (items.length === 0) return defaultChecklist;
    const sections = new Map<string, string[]>();
    items.forEach(it => {
      if (!sections.has(it.section)) sections.set(it.section, []);
      sections.get(it.section)!.push(it.item);
    });
    return Array.from(sections.entries()).map(([section, itemList]) => ({ section, items: itemList }));
  })();
  const [form, setForm] = useState({
    listing_id: '', property_id: '', scheduled_date: '', time_start: '', time_end: '',
    vendor: '', checkout_time: '', checkin_time: '', notes: '', cost: '',
  });

  const handleStatusChange = async (task: Task, newStatus: string) => {
    try {
      await api(`/tenant/minpaku/cleaning/${task.id}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) });
      toast(`ステータスを「${statusLabel[newStatus]}」に変更しました`, 'success');
      refetch();
    } catch { toast('変更に失敗しました', 'error'); }
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    try {
      const body: Record<string, unknown> = {};
      if (editForm.vendor) body.vendor = editForm.vendor;
      if (editForm.time_start) body.time_start = editForm.time_start;
      if (editForm.time_end) body.time_end = editForm.time_end;
      if (editForm.notes !== undefined) body.notes = editForm.notes;
      await api(`/tenant/minpaku/cleaning/${editTarget.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      toast('タスクを更新しました', 'success');
      setShowEdit(false);
      refetch();
    } catch { toast('更新に失敗しました', 'error'); }
  };

  const handleDelete = async (task: Task) => {
    try {
      await api(`/tenant/minpaku/cleaning/${task.id}`, { method: 'DELETE' });
      toast('タスクを削除しました', 'success');
      refetch();
    } catch { toast('削除に失敗しました', 'error'); }
  };

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  const allTasks = tasks ?? [];
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonth = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;

  // 本日のタスク
  const todayTasks = allTasks.filter((t) => (t.scheduled_date ?? '').slice(0, 10) === today);
  const todayIncomplete = todayTasks.filter(t => t.status !== 'completed').length;

  // 緊急
  const urgentTasks = allTasks.filter((t) => t.status === 'urgent' || (t.checkin_time && t.status !== 'completed'));
  const urgentCount = urgentTasks.length;
  const firstUrgentCI = urgentTasks.find(t => t.checkin_time)?.checkin_time;

  // 今月・先月の完了数
  const completedThisMonth = allTasks.filter((t) => t.status === 'completed' && (t.scheduled_date ?? '').slice(0, 7) === thisMonth).length;
  const completedLastMonth = allTasks.filter((t) => t.status === 'completed' && (t.scheduled_date ?? '').slice(0, 7) === lastMonth).length;

  // 平均清掃時間の計算ヘルパー
  const calcAvg = (list: Task[]) => {
    const withTimes = list.filter(t => t.status === 'completed' && t.time_start && t.time_end);
    if (withTimes.length === 0) return null;
    return Math.round(withTimes.reduce((sum, t) => {
      const [sh, sm] = t.time_start!.split(':').map(Number);
      const [eh, em] = t.time_end!.split(':').map(Number);
      return sum + ((eh * 60 + em) - (sh * 60 + sm));
    }, 0) / withTimes.length);
  };

  const thisMonthTasks = allTasks.filter(t => (t.scheduled_date ?? '').slice(0, 7) === thisMonth);
  const lastMonthTasks = allTasks.filter(t => (t.scheduled_date ?? '').slice(0, 7) === lastMonth);
  const avgMinutes = calcAvg(thisMonthTasks) ?? calcAvg(allTasks) ?? 45;
  const prevAvgMinutes = calcAvg(lastMonthTasks);
  const avgDiff = prevAvgMinutes != null ? avgMinutes - prevAvgMinutes : null;

  const propertyNames = [...new Set(allTasks.map((t) => t.property_name))];
  const filtered = allTasks.filter((t) => {
    if (propertyFilter !== 'all' && t.property_name !== propertyFilter) return false;
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    return true;
  });

  const toggleCheck = (key: string) => {
    setCheckedItems((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };

  const handleStaffAdd = async () => {
    if (!staffForm.name) { toast('スタッフ名は必須です', 'error'); return; }
    try {
      await api('/tenant/minpaku/cleaning-staff', {
        method: 'POST',
        body: JSON.stringify({ ...staffForm, cost_per_task: Number(staffForm.cost_per_task) || 0 }),
      });
      toast('スタッフを登録しました', 'success');
      setShowStaffAdd(false);
      setStaffForm({ name: '', phone: '', email: '', type: 'vendor', cost_per_task: '' });
      refetchStaff();
    } catch { toast('登録に失敗しました', 'error'); }
  };

  const handleStaffDelete = async (s: Staff) => {
    try {
      await api(`/tenant/minpaku/cleaning-staff/${s.id}`, { method: 'DELETE' });
      toast('スタッフを削除しました', 'success');
      refetchStaff();
    } catch { toast('削除に失敗しました', 'error'); }
  };

  const handleChecklistEdit = () => {
    setEditChecklist(checklistTemplate.map(s => ({ section: s.section, items: [...s.items] })));
    setEditingChecklist(true);
  };

  const handleChecklistSave = async () => {
    const items: { section: string; item: string; sort_order: number }[] = [];
    editChecklist.forEach((s, si) => {
      s.items.forEach((item, ii) => {
        items.push({ section: s.section, item, sort_order: si * 100 + ii });
      });
    });
    try {
      await api('/tenant/minpaku/cleaning-checklist', { method: 'PUT', body: JSON.stringify(items) });
      toast('チェックリストを保存しました', 'success');
      setEditingChecklist(false);
      refetchChecklist();
    } catch { toast('保存に失敗しました', 'error'); }
  };

  const handleListingSelect = (listingId: string) => {
    const listing = (listings ?? []).find(l => l.id === listingId);
    setForm({ ...form, listing_id: listingId, property_id: listing?.property_id || '' });
  };

  const handleCreate = async () => {
    if (!form.property_id || !form.scheduled_date) { toast('物件と日付は必須です', 'error'); return; }
    try {
      await api('/tenant/minpaku/cleaning', {
        method: 'POST',
        body: JSON.stringify({
          property_id: form.property_id, listing_id: form.listing_id || undefined,
          scheduled_date: form.scheduled_date,
          time_start: form.time_start || null, time_end: form.time_end || null,
          vendor: form.vendor || null, status: form.vendor ? 'scheduled' : 'unassigned',
          checkout_time: form.checkout_time || null, checkin_time: form.checkin_time || null,
          notes: form.notes || null,
          cost: form.cost ? Number(form.cost) : 0,
        }),
      });
      toast('清掃タスクを追加しま���た', 'success');
      setShowCreate(false);
      setForm({ listing_id: '', property_id: '', scheduled_date: '', time_start: '', time_end: '', vendor: '', checkout_time: '', checkin_time: '', notes: '', cost: '' });
      refetch();
    } catch { toast('追加に失敗しました', 'error'); }
  };

  // タスク行のアイコン・スタイル判定
  const getTaskIcon = (t: Task) => {
    if (t.status === 'completed') return { icon: '✅', cls: styles.iconDone };
    if (t.status === 'urgent' || (t.checkin_time && t.status !== 'completed' && (t.scheduled_date ?? '').slice(0, 10) === today)) return { icon: '🚨', cls: styles.iconUrgent };
    if (t.status === 'unassigned') return { icon: '⚠️', cls: styles.iconUnassigned };
    return { icon: '⏳', cls: styles.iconPending };
  };

  const getDeadline = (t: Task) => {
    if (t.status === 'completed' && t.time_end) return `完了 ${fmt(t.time_end)}`;
    if (t.time_end) return `〜${fmt(t.time_end)}`;
    if (!t.vendor) return '未割当';
    return '—';
  };

  return (
    <div className={styles.page}>
      {/* KPI */}
      <div className={styles.kpiRow}>
        <KpiCard label="本日の清掃タスク" value={`${todayTasks.length}`} delta={todayIncomplete > 0 ? `未完了 ${todayIncomplete}件` : ''} trend={todayIncomplete > 0 ? 'down' : 'flat'} />
        <KpiCard label="緊急（CI前）" value={`${urgentCount}件`} delta={firstUrgentCI ? `${fmt(firstUrgentCI)} CIあり` : ''} trend={urgentCount > 0 ? 'down' : 'flat'} />
        <KpiCard label="今月完了数" value={`${completedThisMonth}`} delta={completedLastMonth > 0 ? `↑ 先月${completedLastMonth}件` : ''} trend="up" />
        <KpiCard label="平均清掃時間" value={`${avgMinutes}分`} delta={avgDiff != null && avgDiff !== 0 ? (avgDiff < 0 ? `↑ 効率化 ${avgDiff}分` : `↓ +${avgDiff}分`) : ''} trend={avgDiff != null && avgDiff < 0 ? 'up' : avgDiff != null && avgDiff > 0 ? 'down' : 'flat'} />
      </div>

      {/* ツールバー */}
      <div className={styles.toolbar}>
        <select className={styles.filterSelect} value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)}>
          <option value="all">全物件</option>
          {propertyNames.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">全ステータス</option>
          <option value="scheduled">予定</option><option value="in_progress">作業中</option>
          <option value="completed">完了</option><option value="unassigned">未割当</option>
        </select>
        <span className={styles.spacer} />
        <button className={styles.btnSecondary} onClick={() => setShowStaff(true)}>👤 スタッフ管理</button>
        <button className={styles.btnPrimary} onClick={() => setShowCreate(true)}>＋ タスク追加</button>
      </div>

      {/* タスク一覧 */}
      <Card title={`🧹 本日の清掃タスク（${todayTasks.length}件）`}>
        {filtered.length > 0 ? (
          <div className={styles.taskList}>
            {filtered.map((t) => {
              const { icon, cls } = getTaskIcon(t);
              const isCompleted = t.status === 'completed';
              const isUrgent = t.status === 'urgent' || (t.checkin_time && t.status !== 'completed' && (t.scheduled_date ?? '').slice(0, 10) === today);
              const deadlineColor = isCompleted ? 'var(--green)' : isUrgent ? 'var(--red)' : !t.vendor ? 'var(--amber)' : 'var(--ink2)';

              return (
                <div key={t.id} className={styles.cleanRow} style={isCompleted ? { opacity: 0.6 } : undefined}>
                  <div className={`${styles.cleanIcon} ${cls}`} onClick={() => !isCompleted && handleStatusChange(t, 'completed')}>{icon}</div>
                  <div className={styles.cleanBody}>
                    <div className={styles.cleanProp}>{t.property_name}</div>
                    <div className={styles.cleanMeta}>
                      {(t.scheduled_date ?? '').slice(0, 10) !== today && <span>{(t.scheduled_date ?? '').slice(0, 10).slice(5)} ｜ </span>}
                      CO: {fmt(t.checkout_time)} ｜ 次のCI: {fmt(t.checkin_time)} ｜ 担当: {t.vendor ?? '未割当'}
                    </div>
                  </div>
                  <div className={styles.cleanDeadline} style={{ color: deadlineColor }}>{getDeadline(t)}</div>
                  <div className={styles.cleanActions}>
                    {isUrgent && !isCompleted && (
                      <button className={styles.btnDanger} onClick={() => toast('催促アラートを送信しました', 'success')}>🔔 催促</button>
                    )}
                    {!t.vendor && !isCompleted && (
                      <button className={styles.btnWarn} onClick={() => { setEditTarget(t); setEditForm({ vendor: '', time_start: fmt(t.time_start) === '—' ? '' : fmt(t.time_start), time_end: fmt(t.time_end) === '—' ? '' : fmt(t.time_end), notes: '' }); setShowEdit(true); }}>👤 割当</button>
                    )}
                    {!isCompleted && (
                      <button className={styles.btnPrimary} onClick={() => handleStatusChange(t, 'completed')}>✅ 完了</button>
                    )}
                    {isCompleted && (
                      <button className={styles.btnSecondary} onClick={() => toast('完了レポートを確認します', 'info')}>📷 報告</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.emptyState}>清掃タスクはありません</div>
        )}
      </Card>

      {/* チェックリスト（2列グリッド・モック準拠） */}
      <Card title="📋 清掃チェックリスト（標準テンプレート）" actions={
        editingChecklist
          ? <div style={{ display: 'flex', gap: 6 }}><button className={styles.btnSecondary} onClick={() => setEditingChecklist(false)}>キャンセル</button><button className={styles.btnPrimary} onClick={handleChecklistSave}>保存</button></div>
          : <button className={styles.btnSecondary} onClick={handleChecklistEdit}>✏️ 編集</button>
      }>
        {editingChecklist ? (
          <div className={styles.checkEditGrid}>
            {editChecklist.map((section, si) => (
              <div key={si} className={styles.checkEditSection}>
                <input className={styles.checkEditSectionInput} value={section.section}
                  onChange={e => { const next = [...editChecklist]; next[si] = { ...next[si], section: e.target.value }; setEditChecklist(next); }}
                  placeholder="セクション名" />
                {section.items.map((item, ii) => (
                  <div key={ii} className={styles.checkEditRow}>
                    <input className={styles.checkEditItemInput} value={item}
                      onChange={e => { const next = [...editChecklist]; next[si].items[ii] = e.target.value; setEditChecklist(next); }}
                      placeholder="チェック項目" />
                    <button className={styles.checkEditDelete} onClick={() => {
                      const next = [...editChecklist]; next[si].items.splice(ii, 1); setEditChecklist(next);
                    }}>✕</button>
                  </div>
                ))}
                <button className={styles.checkEditAdd} onClick={() => {
                  const next = [...editChecklist]; next[si].items.push(''); setEditChecklist(next);
                }}>＋ 項目追加</button>
              </div>
            ))}
            <button className={styles.checkEditAddSection} onClick={() => {
              setEditChecklist([...editChecklist, { section: '', items: [''] }]);
            }}>＋ セクション追加</button>
          </div>
        ) : (
          <div className={styles.checkGrid}>
            {checklistTemplate.map((section) => (
              <div key={section.section}>
                <div className={styles.checkSectionTitle}>{section.section}</div>
                {section.items.map((item) => {
                  const key = `${section.section}:${item}`;
                  const isChecked = checkedItems.has(key);
                  return (
                    <label key={key} className={styles.checkItem}>
                      <input type="checkbox" checked={isChecked} onChange={() => toggleCheck(key)} className={styles.nativeCheck} />
                      <span style={isChecked ? { textDecoration: 'line-through', color: 'var(--ink3)' } : undefined}>{item}</span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* タスク追加モーダル */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="清掃タスク追加" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.btn} onClick={() => setShowCreate(false)}>キャンセル</button>
          <button className={styles.btnPrimary} onClick={handleCreate}>追加</button>
        </div>
      }>
        <div className={styles.createForm}>
          <div className={styles.formGroup}><label className={styles.formLabel}>物件 *</label>
            <select className={styles.formInput} value={form.listing_id} onChange={e => handleListingSelect(e.target.value)}>
              <option value="">選択してください</option>
              {(listings ?? []).map(l => <option key={l.id} value={l.id}>{l.property_name}</option>)}
            </select>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>清掃日 *</label>
            <input className={styles.formInput} type="date" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>CO時間</label>
              <input className={styles.formInput} type="time" value={form.checkout_time} onChange={e => setForm({ ...form, checkout_time: e.target.value })} />
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>次CI時間</label>
              <input className={styles.formInput} type="time" value={form.checkin_time} onChange={e => setForm({ ...form, checkin_time: e.target.value })} />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>作業開始</label>
              <input className={styles.formInput} type="time" value={form.time_start} onChange={e => setForm({ ...form, time_start: e.target.value })} />
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>作業終了</label>
              <input className={styles.formInput} type="time" value={form.time_end} onChange={e => setForm({ ...form, time_end: e.target.value })} />
            </div>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>担当者</label>
            <select className={styles.formInput} value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })}>
              <option value="">未割当</option>
              {allStaff.map(s => <option key={s.id} value={s.name}>{s.name}（{s.type === 'internal' ? '社内' : '外部'}{s.cost_per_task > 0 ? ` ¥${s.cost_per_task.toLocaleString()}/回` : ''}）</option>)}
            </select>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>清掃費 (円)</label>
            <input className={styles.formInput} type="number" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} placeholder="8000" />
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>備考</label>
            <input className={styles.formInput} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="特記事項" />
          </div>
        </div>
      </Modal>

      {/* 編集モーダル */}
      <Modal open={showEdit} onClose={() => setShowEdit(false)} title={`清掃タスク編集 — ${editTarget?.property_name ?? ''}`} footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.btn} onClick={() => setShowEdit(false)}>キャンセル</button>
          <button className={styles.btnPrimary} onClick={handleEdit}>保存</button>
        </div>
      }>
        <div className={styles.createForm}>
          <div className={styles.formGroup}><label className={styles.formLabel}>担当者</label>
            <select className={styles.formInput} value={editForm.vendor} onChange={e => setEditForm({ ...editForm, vendor: e.target.value })}>
              <option value="">未割当</option>
              {allStaff.map(s => <option key={s.id} value={s.name}>{s.name}（{s.type === 'internal' ? '社内' : '外部'}{s.cost_per_task > 0 ? ` ¥${s.cost_per_task.toLocaleString()}/回` : ''}）</option>)}
            </select>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>作業開始</label>
              <input className={styles.formInput} type="time" value={editForm.time_start} onChange={e => setEditForm({ ...editForm, time_start: e.target.value })} />
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>作業終了</label>
              <input className={styles.formInput} type="time" value={editForm.time_end} onChange={e => setEditForm({ ...editForm, time_end: e.target.value })} />
            </div>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>備考</label>
            <input className={styles.formInput} value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} placeholder="特記事項" />
          </div>
        </div>
      </Modal>

      {/* スタッフ管理 SlidePanel */}
      <SlidePanel open={showStaff} onClose={() => setShowStaff(false)} title="👤 スタッフ管理" width={520}
        actions={<button className={styles.btnPrimary} onClick={() => setShowStaffAdd(true)}>＋ 追加</button>}>
        {allStaff.length > 0 ? allStaff.map(s => (
          <div key={s.id} style={{ padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{s.name}</span>
                <span style={{ fontSize: 11, color: 'var(--ink3)', marginLeft: 8, padding: '1px 6px', background: s.type === 'internal' ? 'var(--blue-bg)' : 'var(--amber-bg)', borderRadius: 4 }}>
                  {s.type === 'internal' ? '社内' : '外部業者'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className={styles.btnSecondary} style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => { if (confirm(`${s.name}を削除しますか？`)) handleStaffDelete(s); }}>🗑</button>
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink2)', display: 'flex', gap: 16 }}>
              {s.phone && <span>📞 {s.phone}</span>}
              {s.email && <span>📧 {s.email}</span>}
              <span style={{ fontFamily: 'var(--mono)', color: 'var(--brand)' }}>¥{s.cost_per_task.toLocaleString()}/回</span>
            </div>
          </div>
        )) : <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>スタッフが登録されていません</div>}
      </SlidePanel>

      {/* スタッフ追加モーダル */}
      <Modal open={showStaffAdd} onClose={() => setShowStaffAdd(false)} title="清掃スタッフ追加" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.btn} onClick={() => setShowStaffAdd(false)}>キャンセル</button>
          <button className={styles.btnPrimary} onClick={handleStaffAdd}>登録</button>
        </div>
      }>
        <div className={styles.createForm}>
          <div className={styles.formGroup}><label className={styles.formLabel}>スタッフ名 / 業者名 *</label>
            <input className={styles.formInput} value={staffForm.name} onChange={e => setStaffForm({ ...staffForm, name: e.target.value })} placeholder="クリーンプロ" />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>種別</label>
              <select className={styles.formInput} value={staffForm.type} onChange={e => setStaffForm({ ...staffForm, type: e.target.value })}>
                <option value="vendor">外部業者</option>
                <option value="internal">社内スタッフ</option>
              </select>
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>清掃単価（円/回）</label>
              <input className={styles.formInput} type="number" value={staffForm.cost_per_task} onChange={e => setStaffForm({ ...staffForm, cost_per_task: e.target.value })} placeholder="3000" />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>電話番号</label>
              <input className={styles.formInput} value={staffForm.phone} onChange={e => setStaffForm({ ...staffForm, phone: e.target.value })} placeholder="090-1234-5678" />
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>メール</label>
              <input className={styles.formInput} value={staffForm.email} onChange={e => setStaffForm({ ...staffForm, email: e.target.value })} placeholder="staff@example.com" />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
